<template>
  <div class="flowmeter-wrapper">
    <div class="flowmeter-panel">
      <!-- 螺丝装饰 -->
      <div class="screw top-left"></div>
      <div class="screw top-right"></div>
      <div class="screw bottom-left"></div>
      <div class="screw bottom-right"></div>

      <div class="flex items-center gap-6" style="display: flex;">
        <!-- 左侧：数字显示区 -->
        <div class="display-section">
          <!-- 主要数值显示 -->
          <div class="lcd-display total-display" :class="{ 'negative': currentValue < 0 }">
           {{ currentValue > 0 ? '+' : '' }}{{ currentValue.toFixed(3) }}
          </div>
          
          <!-- 单位标识 -->
          <div class="unit-row">
            <span class="unit-label">RANGE: -2000 ~ +2000</span>
          </div>

          <!-- 编号显示 -->
          <div class="instant-row">
            <span class="zero-label">编号</span>
            <div class="lcd-display instant-display">
              {{ pntName || '' }}
            </div>
          </div>
        </div>

        <!-- 分隔线 -->
        <div class="divider"></div>

        <!-- 右侧：液位条 -->
        <div class="bars-section">
          <!-- 主液位条 -->
          <div class="bar-wrapper">
            <!-- 刻度线与标签 -->
            <div class="scale-container">
              <div 
                v-for="(tick, index) in scaleTicks" 
                :key="index"
                :class="['scale-mark', { 'major': tick.isMajor, 'zero': tick.value === 0 }]"
                :style="{ bottom: tick.position + '%' }"
              >
                <span v-if="tick.isMajor || tick.value === 0" class="tick-label">
                  {{ tick.value > 0 ? '+' : '' }}{{ tick.value }}
                </span>
              </div>
            </div>
            
            <!-- 柱状图 -->
            <div class="bar-container">
              <!-- 零线标记 -->
              <div class="zero-line"></div>
              
              <!-- 液位填充 -->
              <div 
                class="liquid-fill"
                :style="{ 
                  height: Math.abs(liquidHeight) + '%',
                  bottom: liquidBottom + '%',
                  background: liquidColor
                }"
                :class="{ 'blink': Math.abs(currentValue) > 380 }"
              ></div>
              
              <!-- 内部网格线 -->
              <div class="inner-grid">
                <div class="grid-line" style="top: 25%"></div>
                <div class="grid-line" style="top: 50%"></div>
                <div class="grid-line" style="top: 75%"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'IndustrialFlowmeter',
  
  props: {
    // 实时数值 (-2000 到 +2000)
    value: {
      type: Number,
      default: 0
    },
    // 单位
    pntName: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      currentValue: 0,      // 当前显示值（平滑过渡后的工程值）
      targetValue: 0,       // 目标工程值
      animationFrame: null
    }
  },

  computed: {
    // 计算液位条显示参数
    liquidHeight() {
      // 将 -2000~+2000 映射到 0~50% 的高度（以中心为基准向上下延伸）
      return (Math.abs(this.currentValue) / 2000) * 50
    },
    
    liquidBottom() {
      // 计算底部位置：负值向下（0-50%），正值向上（50-100%）
      if (this.currentValue >= 0) {
        return 50 // 从中心向上
      } else {
        return 50 - this.liquidHeight // 从中心向下
      }
    },
    
    // 根据正负值显示不同颜色（可选）
    liquidColor() {
      if (this.currentValue < 0) {
        return 'linear-gradient(0deg, #dc2626 0%, #ef4444 50%, #f87171 100%)' // 红色表示负值
      }
      return 'linear-gradient(0deg, #2d8a3e 0%, #4ade80 50%, #22c55e 100%)' // 绿色表示正值
    },
    
    // 生成刻度数据：-2000, -1500, -1000, -500, 0, +500, +1000, +1500, +2000
    scaleTicks() {
      const ticks = []
      const range = 2000
      const steps = 8 // 分成8等份，每份500
      
      for (let i = 0; i <= steps; i++) {
        const value = -range + (range * 2 / steps) * i
        const position = (i / steps) * 100
        
        ticks.push({
          value: Math.round(value),
          position: position,
          isMajor: i % 2 === 0 // 每隔一个显示大刻度
        })
      }
      return ticks
    }
  },

  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        // 限制范围 -2000 到 +2000
        this.targetValue = Math.max(-2000, Math.min(2000, newVal))
      }
    }
  },

  mounted() {
    this.startAnimation()
    this.targetValue = this.value
    this.currentValue = this.value
  },

  beforeDestroy() {
    this.stopAnimation()
  },

  methods: {
    // 平滑动画循环
    startAnimation() {
      const animate = () => {
        const diff = this.targetValue - this.currentValue
        if (Math.abs(diff) > 0.5) {
          this.currentValue += diff * 0.1
        } else {
          this.currentValue = this.targetValue
        }
        
        this.animationFrame = requestAnimationFrame(animate)
      }
      animate()
    },

    stopAnimation() {
      if (this.animationFrame) {
        cancelAnimationFrame(this.animationFrame)
      }
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Share+Tech+Mono&display=swap');

.flowmeter-wrapper {
  font-family: 'Share Tech Mono', monospace;
  display: inline-block;
  padding: 20px;
  background: #3a3a3a;
  border-radius: 8px;
}

.flowmeter-panel {
  background: linear-gradient(90deg, #d4d4d4 0px, #e0e0e0 1px, #d4d4d4 2px);
  background-size: 4px 100%;
  padding: 30px;
  border-radius: 4px;
  box-shadow: 
    inset 0 0 20px rgba(0,0,0,0.2),
    0 4px 6px rgba(0,0,0,0.3);
  position: relative;
  border: 2px solid #999;
}

/* 螺丝 */
.screw {
  position: absolute;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: radial-gradient(circle at 30% 30%, #666, #333);
  box-shadow: 0 1px 2px rgba(0,0,0,0.5);
}
.screw::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 80%;
  height: 2px;
  background: #222;
  transform: translate(-50%, -50%) rotate(45deg);
}
.screw::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 80%;
  height: 2px;
  background: #222;
  transform: translate(-50%, -50%) rotate(-45deg);
}
.top-left { top: 8px; left: 8px; }
.top-right { top: 8px; right: 8px; }
.bottom-left { bottom: 8px; left: 8px; }
.bottom-right { bottom: 8px; right: 8px; }

/* 显示区域 */
.display-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 200px;
}

/* LCD屏幕 */
.lcd-display {
  background: #9ea792;
  color: #2b2b2b;
  padding: 8px 12px;
  border-radius: 4px;
  font-weight: bold;
  box-shadow: 
    inset 2px 2px 5px rgba(0,0,0,0.3),
    inset -1px -1px 2px rgba(255,255,255,0.3);
  text-shadow: 0 0 2px rgba(0,0,0,0.1);
  font-variant-numeric: tabular-nums;
  border: 1px solid #7a7a7a;
}

.total-display {
  font-size: 28px;
  text-align: right;
  letter-spacing: 1px;
}

.total-display.negative {
  color: #991b1b; /* 负值时变红 */
}

.instant-display {
  font-size: 20px;
  text-align: right;
  flex: 1;
}

.unit-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  padding: 0 8px;
}

.unit-label {
  font-size: 10px;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.instant-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
}

.zero-label {
  font-size: 14px;
  color: #555;
  font-weight: bold;
}

/* 分隔线 */
.divider {
  width: 2px;
  height: 160px;
  background: #999;
  box-shadow: 1px 0 1px rgba(255,255,255,0.5);
  margin: 0 20px;
  margin-right: 40px;
}

/* 液位条区域 */
.bars-section {
  display: flex;
  gap: 8px;
  align-items: flex-end;
  padding-left: 12px;
}

.bar-wrapper {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.scale-container {
  position: absolute;
  left: -35px;
  top: 2px;
  bottom: 2px;
  width: 30px;
}

.scale-mark {
  position: absolute;
  left: 20px;
  width: 6px;
  height: 1px;
  background: #555;
  transform: translateY(50%);
}

.scale-mark.major {
  width: 10px;
  height: 2px;
  background: #333;
}

.scale-mark.zero {
  width: 12px;
  height: 3px;
  background: #222;
  box-shadow: 0 0 2px rgba(0,0,0,0.5);
}

.tick-label {
  position: absolute;
  right: 15px;
  top: -6px;
  font-size: 10px;
  color: #333;
  font-weight: bold;
  white-space: nowrap;
}

.bar-container {
  width: 32px;
  height: 160px;
  background: #e8e6d1;
  border: 2px solid #999;
  border-radius: 2px;
  box-shadow: 
    inset 3px 3px 6px rgba(0,0,0,0.4),
    inset -1px -1px 2px rgba(255,255,255,0.8),
    2px 2px 4px rgba(0,0,0,0.3);
  position: relative;
  overflow: hidden;
}

/* 零线标记 */
.zero-line {
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  height: 2px;
  background: #333;
  z-index: 2;
  box-shadow: 0 0 2px rgba(0,0,0,0.3);
}

.zero-line::before {
  content: '0';
  position: absolute;
  right: -18px;
  top: -7px;
  font-size: 10px;
  font-weight: bold;
  color: #333;
}

.liquid-fill {
  position: absolute;
  left: 0;
  width: 100%;
  box-shadow: 
    0 0 10px rgba(74, 222, 128, 0.3),
    inset 0 2px 4px rgba(255,255,255,0.3);
  transition: height 0.3s ease-out, bottom 0.3s ease-out;
}

.liquid-fill::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: rgba(255,255,255,0.6);
  box-shadow: 0 0 4px rgba(255,255,255,0.8);
}

.blink {
  animation: blink-animation 1s infinite;
}

@keyframes blink-animation {
  0%, 50% { opacity: 1; }
  51%, 100% { opacity: 0.4; }
}

.inner-grid {
  position: absolute;
  inset: 0;
  opacity: 0.15;
  pointer-events: none;
}

.grid-line {
  position: absolute;
  width: 100%;
  height: 1px;
  background: #000;
}
</style>